import React, { useState, useRef, useEffect } from 'react';
import { X, ChevronDown, ChevronUp } from 'lucide-react';
import Image from '@/components/Image';
import { colors } from '@/constant/colors';
import useAuthStore from '@/store/useAuthStore';
import { apiClient } from '@/lib/api-client';
import report, { ReportEvent } from '@/lib/report';
import posthog from 'posthog-js';
import useConfigStore from '@/store/useConfigStore';

interface ReportPopupProps {
  isOpen: boolean;
  onClose: () => void;
  elementTitle: string;
  lessonTitle?: string;
  specificElement?: any; // Add specific element prop
  reportType?: string; // "element" or "practice"
  questionId?: string; // For practice reports
  // Context IDs - should be explicitly passed by the calling component
  contextQuestId?: string;
  contextChapterId?: string;
  contextLessonId?: string;
}

const ReportPopup: React.FC<ReportPopupProps> = ({
  isOpen,
  onClose,
  elementTitle,
  lessonTitle,
  specificElement,
  reportType = "element",
  questionId,
  contextQuestId,
  contextChapterId,
  contextLessonId
}) => {
  const [problemDescription, setProblemDescription] = useState('');
  const [uploadedImages, setUploadedImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedIssues, setSelectedIssues] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const lastSubmitTimeRef = useRef<number>(0);

  const user = useAuthStore((state) => state.user);

  const issueOptions = [
    "The answer seems wrong.",
    "My answer matched but was marked wrong.", 
    "The question doesn't show correctly.",
    "The question feels too hard."
  ];

  const handleIssueToggle = (issue: string) => {
    setSelectedIssues(prev => 
      prev.includes(issue) 
        ? prev.filter(item => item !== issue)
        : [...prev, issue]
    );
  };

  useEffect(() => {
    if (!isOpen) return;
    useConfigStore.setState({ feedbackInput: true });
  }, [isOpen]);

  const handleClose = () => {
    useConfigStore.setState({ feedbackInput: false });
    // Reset all form state when closing
    setProblemDescription('');
    setUploadedImages([]);
    setImagePreviews([]);
    setSelectedIssues([]);
    setIsSubmitted(false);
    setIsSubmitting(false);
    onClose();
  };

  const handleSuccessClose = () => {
    handleClose();
  };

  const processFile = (file: File) => {
    if (file && file.type.startsWith('image/')) {
      setUploadedImages(prev => [...prev, file]);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews(prev => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      Array.from(files).forEach(file => processFile(file));
      // Reset the input value so the same file can be selected again
      event.target.value = '';
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      Array.from(files).forEach(file => processFile(file));
    }
  };

  const handleRemoveImage = (index: number) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleSendReport = async () => {
    if (!user) {
      alert('Please log in to submit a report');
      return;
    }

    // Debounce: prevent multiple submissions within 3 seconds
    const now = Date.now();
    if (now - lastSubmitTimeRef.current < 3000) {
      console.log('Report submission throttled - please wait');
      return;
    }

    // Check if already submitting
    if (isSubmitting) {
      console.log('Already submitting report');
      return;
    }

    // Remove validation - allow empty reports

    setIsSubmitting(true);
    lastSubmitTimeRef.current = now;

    try {
      // Step 1: Upload images to Cloudflare R2
      const uploadedUrls: string[] = [];
      
      for (const image of uploadedImages) {
        const formData = new FormData();
        formData.append('file', image);
        formData.append('type', 'bug_report_screenshot');
        formData.append('userId', user.id);

        const uploadResponse = await apiClient.postForm('/api/upload/direct', formData);
        
        if (!uploadResponse.ok) {
          const errorData = await uploadResponse.json().catch(() => ({}));
          throw new Error(`Failed to upload image: ${errorData.error || 'Upload failed'}`);
        }

        const uploadData = await uploadResponse.json();
        uploadedUrls.push(uploadData.url);
      }

      // Get session replay URL for debugging (look back 60 seconds)
      let sessionReplayUrl = null;
      try {
        sessionReplayUrl = posthog.get_session_replay_url?.({
          withTimestamp: true,
          timestampLookBack: 60
        });
      } catch (error) {
        console.log('PostHog session replay URL not available:', error);
      }

      // Step 2: Save report to Supabase
      // Prefer explicit page context IDs to ensure report metadata matches user location.
      // Fallback to element-level IDs only when context is unavailable.
      const reportData = {
        user_id: user.id,
        user_email: user.email || '',
        quest_id: contextQuestId || specificElement?.quest_id || null,
        chapter_id: contextChapterId || specificElement?.chapter_id || null,
        lesson_id: contextLessonId || specificElement?.lesson_id || null,
        element_id: specificElement?.id || null,
        problem_description: problemDescription.trim() || null,
        screenshot_urls: uploadedUrls || [],
        report_type: reportType,
        question_id: questionId || null,
        session_replay_url: sessionReplayUrl, // Add session replay URL
        selected_issues: selectedIssues.length > 0 ? selectedIssues : null // Add selected issues
      };

      const saveResponse = await apiClient.post('/api/submit-report', reportData);

      if (!saveResponse.ok) {
        const errorData = await saveResponse.json().catch(() => ({}));
        throw new Error(`Failed to submit report: ${errorData.error || 'Submission failed'}`);
      }

      // Success - show success message
      setIsSubmitted(true);

      report(ReportEvent.USER_REPORT_BUTTON_COMPLETE, {});
    } catch (error) {
      console.error('Error submitting report:', error);
      alert(error instanceof Error ? error.message : 'Failed to submit report. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  if (isSubmitted) {
    return (
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ backgroundColor: 'rgba(0, 0, 0, 0.4)' }}>
        <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full">
          <div className="p-8 text-center">
            <div className="mb-6">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-xl font-bold mb-2" style={{ color: colors.homeTextBlackColor }}>
                Report submitted successfully!
              </h2>
              <p className="text-base" style={{ color: colors.mainTextColor }}>
                Thank you for your feedback.
              </p>
            </div>
            <button
              onClick={handleSuccessClose}
              className="emphasize-text h-[40px] rounded-full text-white flex flex-row items-center justify-center text-xl hover:opacity-90 transition-all duration-200 px-8 mx-auto"
              style={{ 
                backgroundColor: colors.secondaryColor
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ backgroundColor: 'rgba(0, 0, 0, 0.4)' }}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full">
        {/* Header */}
        <div className="relative p-6 pb-4">
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-1.5 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
          <div className="text-center">
            <h2 className="display-text-bold text-xl font-bold mb-2" style={{ color: colors.homeTextBlackColor }}></h2>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 pb-6">
          {/* Issue selection */}
          <div className="mb-6 flex flex-col items-center">
            <h3 className="display-text-bold text-lg font-bold mb-3" style={{ color: colors.homeTextBlackColor }}>
              What's wrong with this question?
            </h3>
            <div className="space-y-2">
              {issueOptions.map((issue, index) => (
                <label key={index} className="flex items-center gap-3 cursor-pointer p-2 rounded-lg hover:bg-gray-50 transition-colors">
                  <input
                    type="checkbox"
                    checked={selectedIssues.includes(issue)}
                    onChange={() => handleIssueToggle(issue)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="display-text text-base" style={{ color: colors.mainTextColor }}>
                    {issue}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Send button */}
          <div className="flex justify-center mb-6">
            <button
              onClick={handleSendReport}
              disabled={isSubmitting}
              className="emphasize-text h-[40px] rounded-full text-white flex flex-row items-center justify-center text-xl hover:opacity-90 transition-all duration-200 px-8 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ 
                backgroundColor: colors.secondaryColor
              }}
            >
              {isSubmitting ? 'Submitting...' : 'Send Report'}
            </button>
          </div>

          {/* Collapsible section */}
          <div className="border-t border-gray-200 pt-4">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center justify-center w-full gap-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              <span className="text-sm">Add details (optional)</span>
            </button>

            <div 
              className={`overflow-hidden transition-all duration-300 ease-in-out ${
                isExpanded ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              <div className="mt-4 space-y-6">
                {/* Description section */}
                <div>
                  <label className="text-base font-bold mb-3 block" style={{ color: colors.homeTextBlackColor }}>Problem Description</label>
                  <textarea
                    value={problemDescription}
                    onChange={(e) => setProblemDescription(e.target.value)}
                    placeholder="Help us improve (optional)"
                    className="w-full min-h-[80px] p-4 border border-gray-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-transparent transition-all"
                    style={{ 
                      fontSize: '14px',
                      lineHeight: '1.5',
                      color: colors.homeTextBlackColor,
                      backgroundColor: '#FEFEFE'
                    }}
                  />
                </div>

                {/* Screenshot section */}
                <div>
                  <label className="text-base font-bold mb-3 block" style={{ color: colors.homeTextBlackColor }}>
                    Screenshot <span className="font-normal" style={{ color: '#9CA3AF' }}>(optional)</span>
                  </label>
                  
                  {imagePreviews.length > 0 && (
                    <div className="mb-3">
                      <div className="flex flex-wrap gap-3">
                        {imagePreviews.map((preview, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={preview}
                              alt={`Screenshot preview ${index + 1}`}
                              className="w-20 h-20 object-cover rounded-lg border border-gray-200"
                            />
                            <button
                              onClick={() => handleRemoveImage(index)}
                              className="absolute -top-2 -right-2 bg-white rounded-full p-1 shadow-lg hover:shadow-xl transition-shadow opacity-0 group-hover:opacity-100"
                            >
                              <X className="w-3 h-3 text-gray-600" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div 
                    ref={dropZoneRef}
                    className={`w-full rounded-xl border-2 border-dashed transition-all cursor-pointer ${
                      isDragging 
                        ? 'border-blue-400 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                    onClick={() => fileInputRef.current?.click()}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                  >
                    <div className="p-6 flex items-center justify-center gap-3">
                      <svg className="w-6 h-6" style={{ color: colors.mainTextColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <div className="flex flex-col">
                        <span className="text-sm font-medium" style={{ color: colors.homeTextBlackColor }}>
                          Upload or drag and drop
                        </span>
                        <span className="text-xs" style={{ color: '#9CA3AF' }}>
                          PNG, JPG up to 10MB
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportPopup;
